import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  getCustomers, 
  getProducts, 
  getInvoiceById, 
  createInvoice, 
  updateInvoice, 
  createCustomer 
} from '../../services/api';

const InvoiceForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [newCustomerDialogOpen, setNewCustomerDialogOpen] = useState(false);
  
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [vatRate, setVatRate] = useState(15);
  
  const [newCustomerData, setNewCustomerData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    customer_type: 'retail'
  });
  
  const [formData, setFormData] = useState({
    customer_id: '',
    customer_name: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: getDefaultDueDate(),
    notes: '',
    discount: 0,
    discount_type: 'amount',
    status: 'draft',
    payment_status: 'pending',
    payment_method: 'cash',
    terms: 'Net 30',
    items: []
  });
  
  const [newItem, setNewItem] = useState({
    product_id: '',
    quantity: 1,
    unit_price: 0,
    discount_percentage: 0,
    warranty_months: 12,
    serial_numbers: []
  });

  // Function to calculate default due date (30 days from today)
  function getDefaultDueDate() {
    const today = new Date();
    const dueDate = new Date(today);
    dueDate.setDate(dueDate.getDate() + 30);
    return dueDate.toISOString().split('T')[0];
  }

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [customersResponse, productsResponse] = await Promise.all([
        getCustomers(),
        getProducts()
      ]);
      
      if (customersResponse.data.success) {
        setCustomers(customersResponse.data.data);
      }
      
      if (productsResponse.data.success) {
        const productsData = productsResponse.data.data;
        setProducts(productsData);
        setFilteredProducts(productsData);
      }
      
      if (isEditMode) {
        const invoiceResponse = await getInvoiceById(id);
        if (invoiceResponse.data.success) {
          const invoice = invoiceResponse.data.data;
          const invoiceCustomer = customersResponse.data.success 
            ? customersResponse.data.data.find(c => c.id === invoice.customer_id)
            : null;
          
          setFormData({
            customer_id: invoice.customer_id,
            customer_name: invoiceCustomer ? `${invoiceCustomer.name} - ${invoiceCustomer.email}` : '',
            invoice_date: invoice.invoice_date ? invoice.invoice_date.split('T')[0] : new Date().toISOString().split('T')[0],
            due_date: invoice.due_date ? invoice.due_date.split('T')[0] : getDefaultDueDate(),
            notes: invoice.notes || '',
            discount: parseFloat(invoice.discount) || 0,
            discount_type: invoice.discount_type || 'amount',
            status: invoice.status || 'draft',
            payment_status: invoice.payment_status || 'pending',
            payment_method: invoice.payment_method || 'cash',
            terms: invoice.terms || 'Net 30',
            items: invoice.items || []
          });
          
          setVatRate(invoice.vat_rate || 15);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleVatRateChange = (e) => {
    const newRate = parseFloat(e.target.value) || 0;
    setVatRate(newRate);
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + calculateItemTotal(item), 0);
    const discountAmount = formData.discount_type === 'percentage' 
      ? subtotal * (parseFloat(formData.discount) || 0) / 100
      : parseFloat(formData.discount) || 0;
    
    const taxableAmount = subtotal - discountAmount;
    const tax_amount = taxableAmount * (vatRate / 100);
    const total = taxableAmount + tax_amount;
    
    return {
      subtotal: subtotal.toFixed(2),
      discount_amount: discountAmount.toFixed(2),
      taxable_amount: taxableAmount.toFixed(2),
      tax_amount: tax_amount.toFixed(2),
      total: total.toFixed(2)
    };
  };

  const handleCustomerSelect = (event, value) => {
    if (value && value.id) {
      setFormData(prev => ({
        ...prev,
        customer_id: value.id,
        customer_name: `${value.name} - ${value.email}`
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        customer_id: '',
        customer_name: value || ''
      }));
    }
  };

  const handleCustomerInputChange = (event, value) => {
    setFormData(prev => ({
      ...prev,
      customer_name: value
    }));
  };

  const handleAddNewCustomer = async () => {
    try {
      if (!newCustomerData.name.trim()) {
        setError('Customer name is required');
        return;
      }

      const response = await createCustomer(newCustomerData);
      if (response.data.success) {
        const customersResponse = await getCustomers();
        if (customersResponse.data.success) {
          setCustomers(customersResponse.data.data);
        }
        
        const newCustomer = response.data.data;
        setFormData(prev => ({ 
          ...prev, 
          customer_id: newCustomer.id,
          customer_name: `${newCustomer.name} - ${newCustomer.email}`
        }));
        
        setNewCustomerData({
          name: '',
          email: '',
          phone: '',
          address: '',
          city: '',
          customer_type: 'retail'
        });
        setNewCustomerDialogOpen(false);
        
        setSuccess('New customer added successfully!');
      } else {
        setError(response.data.message || 'Failed to add customer');
      }
    } catch (err) {
      setError(err.message || 'Failed to add new customer');
    }
  };

  const handleProductSearch = (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setFilteredProducts(products);
      return;
    }
    
    const filtered = products.filter(product =>
      product.name.toLowerCase().includes(query.toLowerCase()) ||
      product.code?.toLowerCase().includes(query.toLowerCase()) ||
      product.category?.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredProducts(filtered);
  };

  const handleSelectProductFromSearch = (product) => {
    setNewItem(prev => ({
      ...prev,
      product_id: product.id,
      unit_price: parseFloat(product.price) || 0,
      warranty_months: product.warranty_months || 12
    }));
    setSearchDialogOpen(false);
    setSearchQuery('');
  };

  const calculateItemTotal = (item) => {
    const subtotal = (item.quantity || 0) * (item.unit_price || 0);
    const discount = subtotal * ((item.discount_percentage || 0) / 100);
    return subtotal - discount;
  };

  const handleAddItem = () => {
    if (!newItem.product_id) {
      setError('Please select a product');
      return;
    }
    
    const selectedProduct = products.find(p => p.id === parseInt(newItem.product_id));
    if (!selectedProduct) {
      setError('Selected product not found');
      return;
    }
    
    // Check if product already exists in items
    const existingItemIndex = formData.items.findIndex(item => item.product_id === newItem.product_id);
    
    let updatedItems;
    if (existingItemIndex >= 0) {
      updatedItems = [...formData.items];
      updatedItems[existingItemIndex] = {
        ...updatedItems[existingItemIndex],
        quantity: updatedItems[existingItemIndex].quantity + parseInt(newItem.quantity),
        serial_numbers: [
          ...updatedItems[existingItemIndex].serial_numbers,
          ...newItem.serial_numbers
        ].filter(Boolean)
      };
    } else {
      const item = {
        product_id: parseInt(newItem.product_id),
        product_name: selectedProduct.name,
        product_code: selectedProduct.code || '',
        quantity: parseInt(newItem.quantity) || 1,
        unit_price: parseFloat(newItem.unit_price) || parseFloat(selectedProduct.price),
        discount_percentage: parseFloat(newItem.discount_percentage) || 0,
        warranty_months: newItem.warranty_months || selectedProduct.warranty_months || 12,
        serial_numbers: newItem.serial_numbers.filter(sn => sn.trim() !== '')
      };
      updatedItems = [...formData.items, item];
    }
    
    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
    
    setNewItem({
      product_id: '',
      quantity: 1,
      unit_price: 0,
      discount_percentage: 0,
      warranty_months: 12,
      serial_numbers: []
    });
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleProductChange = (e) => {
    const productId = e.target.value;
    const selectedProduct = products.find(p => p.id === parseInt(productId));
    if (selectedProduct) {
      setNewItem(prev => ({
        ...prev,
        product_id: productId,
        unit_price: parseFloat(selectedProduct.price) || 0,
        warranty_months: selectedProduct.warranty_months || 12
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_id) {
      setError('Please select a customer');
      return;
    }
    
    if (formData.items.length === 0) {
      setError('Please add at least one item');
      return;
    }
    
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      const totals = calculateTotals();
      
      const invoiceData = {
        customer_id: parseInt(formData.customer_id),
        invoice_date: formData.invoice_date,
        due_date: formData.due_date,
        discount: parseFloat(formData.discount) || 0,
        discount_type: formData.discount_type,
        vat_rate: vatRate,
        status: formData.status,
        payment_status: formData.payment_status,
        payment_method: formData.payment_method,
        terms: formData.terms,
        notes: formData.notes,
        subtotal: parseFloat(totals.subtotal),
        tax_amount: parseFloat(totals.tax_amount),
        total_amount: parseFloat(totals.total),
        items: formData.items.map(item => ({
          product_id: parseInt(item.product_id),
          quantity: parseInt(item.quantity),
          unit_price: parseFloat(item.unit_price),
          discount_percentage: parseFloat(item.discount_percentage) || 0,
          warranty_months: parseInt(item.warranty_months) || 12,
          serial_numbers: item.serial_numbers.filter(sn => sn && sn.trim() !== '')
        }))
      };
      
      console.log('Submitting invoice data:', invoiceData);
      
      let response;
      if (isEditMode) {
        response = await updateInvoice(id, invoiceData);
      } else {
        response = await createInvoice(invoiceData);
      }
      
      if (response.data.success) {
        setSuccess(isEditMode ? 'Invoice updated successfully!' : 'Invoice created successfully!');
        
        setTimeout(() => {
          navigate('/invoices');
        }, 1500);
      } else {
        setError(response.data.message || 'Failed to save invoice');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save invoice');
    } finally {
      setSaving(false);
    }
  };

  const totals = calculateTotals();

  const NewCustomerDialog = () => (
    <Dialog open={newCustomerDialogOpen} onClose={() => setNewCustomerDialogOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PersonAddIcon />
          Add New Customer
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Customer Name *"
              value={newCustomerData.name}
              onChange={(e) => setNewCustomerData(prev => ({ ...prev, name: e.target.value }))}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={newCustomerData.email}
              onChange={(e) => setNewCustomerData(prev => ({ ...prev, email: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Phone Number"
              value={newCustomerData.phone}
              onChange={(e) => setNewCustomerData(prev => ({ ...prev, phone: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Address"
              multiline
              rows={2}
              value={newCustomerData.address}
              onChange={(e) => setNewCustomerData(prev => ({ ...prev, address: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="City"
              value={newCustomerData.city}
              onChange={(e) => setNewCustomerData(prev => ({ ...prev, city: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Customer Type</InputLabel>
              <Select
                label="Customer Type"
                value={newCustomerData.customer_type}
                onChange={(e) => setNewCustomerData(prev => ({ ...prev, customer_type: e.target.value }))}
              >
                <MenuItem value="retail">Retail</MenuItem>
                <MenuItem value="wholesale">Wholesale</MenuItem>
                <MenuItem value="corporate">Corporate</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setNewCustomerDialogOpen(false)}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={handleAddNewCustomer}
          disabled={!newCustomerData.name.trim()}
        >
          Add Customer
        </Button>
      </DialogActions>
    </Dialog>
  );

  const ProductSearchDialog = () => (
    <Dialog open={searchDialogOpen} onClose={() => setSearchDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SearchIcon />
          Search Products
        </Box>
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label="Search products by name, code, or category"
          value={searchQuery}
          onChange={(e) => handleProductSearch(e.target.value)}
          margin="normal"
          autoFocus
        />
        
        <TableContainer sx={{ mt: 2, maxHeight: 400 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Category</TableCell>
                <TableCell align="right">Price (LKR)</TableCell>
                <TableCell align="right">Stock</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProducts.map((product) => (
                <TableRow key={product.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {product.name}
                    </Typography>
                  </TableCell>
                  <TableCell>{product.code || '-'}</TableCell>
                  <TableCell>{product.category || '-'}</TableCell>
                  <TableCell align="right">{parseFloat(product.price).toFixed(2)}</TableCell>
                  <TableCell align="right">{product.quantity}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleSelectProductFromSearch(product)}
                      disabled={product.quantity <= 0}
                    >
                      {product.quantity <= 0 ? 'Out of Stock' : 'Select'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setSearchDialogOpen(false)}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/invoices')}
        sx={{ mb: 3 }}
      >
        Back to Invoices
      </Button>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          {isEditMode ? 'Edit Invoice' : 'Create New Invoice'}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Customer and Dates */}
            <Grid item xs={12} md={4}>
              <Box display="flex" flexDirection="column" gap={1}>
                <Autocomplete
                  freeSolo
                  options={customers}
                  getOptionLabel={(option) => 
                    typeof option === 'string' 
                      ? option 
                      : `${option.name} - ${option.email}`
                  }
                  value={formData.customer_name}
                  onChange={handleCustomerSelect}
                  onInputChange={handleCustomerInputChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Customer *"
                      required
                      placeholder="Search or select customer"
                      error={!formData.customer_id}
                      helperText={!formData.customer_id ? "Please select a customer" : ""}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box>
                        <Typography variant="body2">{option.name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {option.email} | {option.phone || 'No phone'}
                        </Typography>
                      </Box>
                    </li>
                  )}
                />
                <Button
                  variant="outlined"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setNewCustomerDialogOpen(true)}
                  fullWidth
                >
                  Add New Customer
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Invoice Date"
                value={formData.invoice_date}
                onChange={(e) => setFormData(prev => ({ ...prev, invoice_date: e.target.value }))}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Due Date"
                value={formData.due_date}
                onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Payment Information */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Payment Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Payment Method</InputLabel>
                        <Select
                          label="Payment Method"
                          value={formData.payment_method}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            payment_method: e.target.value
                          }))}
                        >
                          <MenuItem value="cash">Cash</MenuItem>
                          <MenuItem value="card">Card</MenuItem>
                          <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                          <MenuItem value="cheque">Cheque</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        select
                        fullWidth
                        label="Payment Status"
                        value={formData.payment_status}
                        onChange={(e) => setFormData(prev => ({ ...prev, payment_status: e.target.value }))}
                      >
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="partial">Partial</MenuItem>
                        <MenuItem value="paid">Paid</MenuItem>
                      </TextField>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Terms"
                        value={formData.terms}
                        onChange={(e) => setFormData(prev => ({ ...prev, terms: e.target.value }))}
                        placeholder="e.g., Net 30, Net 60, etc."
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Add Items Section */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">
                      Add Items
                    </Typography>
                    <Button
                      startIcon={<SearchIcon />}
                      variant="outlined"
                      onClick={() => setSearchDialogOpen(true)}
                    >
                      Search Products
                    </Button>
                  </Box>
                  <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} md={2}>
                      <FormControl fullWidth>
                        <InputLabel>Product</InputLabel>
                        <Select
                          label="Product"
                          value={newItem.product_id}
                          onChange={handleProductChange}
                        >
                          <MenuItem value="">Select Product</MenuItem>
                          {products.map((product) => (
                            <MenuItem key={product.id} value={product.id}>
                              {product.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={6} md={1}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Qty"
                        value={newItem.quantity}
                        onChange={(e) => setNewItem(prev => ({ ...prev, quantity: e.target.value }))}
                        inputProps={{ min: 1 }}
                      />
                    </Grid>
                    
                    <Grid item xs={6} md={2}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Unit Price (LKR)"
                        value={newItem.unit_price}
                        onChange={(e) => setNewItem(prev => ({ ...prev, unit_price: e.target.value }))}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </Grid>
                    
                    <Grid item xs={6} md={1}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Disc %"
                        value={newItem.discount_percentage}
                        onChange={(e) => setNewItem(prev => ({ ...prev, discount_percentage: e.target.value }))}
                        inputProps={{ min: 0, max: 100, step: 0.01 }}
                      />
                    </Grid>
                    
                    <Grid item xs={6} md={2}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Warranty (Months)"
                        value={newItem.warranty_months}
                        onChange={(e) => setNewItem(prev => ({ ...prev, warranty_months: e.target.value }))}
                        inputProps={{ min: 0, max: 120 }}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        label="Serial Numbers"
                        placeholder="SN1, SN2, SN3"
                        value={newItem.serial_numbers.join(', ')}
                        onChange={(e) => setNewItem(prev => ({ 
                          ...prev, 
                          serial_numbers: e.target.value.split(',').map(sn => sn.trim()).filter(Boolean)
                        }))}
                        helperText="Separate by commas"
                      />
                    </Grid>
                    
                    <Grid item xs={6} md={1}>
                      <Typography variant="body2" align="right">
                        LKR {calculateItemTotal(newItem).toFixed(2)}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={6} md={1}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddItem}
                        disabled={!newItem.product_id}
                      >
                        Add
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Items Table */}
            {formData.items.length > 0 && (
              <Grid item xs={12}>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Disc %</TableCell>
                        <TableCell align="right">Warranty</TableCell>
                        <TableCell>Serial Numbers</TableCell>
                        <TableCell align="right">Line Total</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {item.product_name}
                            </Typography>
                            {item.product_code && (
                              <Typography variant="caption" color="textSecondary">
                                Code: {item.product_code}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right">{parseFloat(item.unit_price).toFixed(2)}</TableCell>
                          <TableCell align="right">{item.discount_percentage}%</TableCell>
                          <TableCell align="right">{item.warranty_months} months</TableCell>
                          <TableCell>
                            <Box sx={{ maxWidth: 200 }}>
                              {item.serial_numbers && item.serial_numbers.map((serial, serialIndex) => (
                                <Chip
                                  key={serialIndex}
                                  label={serial}
                                  size="small"
                                  sx={{ m: 0.5 }}
                                />
                              ))}
                            </Box>
                          </TableCell>
                          <TableCell align="right">{calculateItemTotal(item).toFixed(2)}</TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveItem(index)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}

            {/* Notes and Totals */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes or terms for the invoice..."
              />
            </Grid>
            
            {/* Totals with Editable VAT */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Invoice Summary
                  </Typography>
                  
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography>Subtotal:</Typography>
                    <Typography>LKR {totals.subtotal}</Typography>
                  </Box>
                  
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography>Discount:</Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <FormControl size="small" sx={{ width: 120 }}>
                        <Select
                          value={formData.discount_type}
                          onChange={(e) => setFormData(prev => ({ ...prev, discount_type: e.target.value }))}
                        >
                          <MenuItem value="amount">Amount</MenuItem>
                          <MenuItem value="percentage">Percentage</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        type="number"
                        value={formData.discount}
                        onChange={(e) => setFormData(prev => ({ ...prev, discount: e.target.value }))}
                        inputProps={{ min: 0, step: 0.01 }}
                        sx={{ width: 100 }}
                      />
                    </Box>
                  </Box>
                  
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography>Discount Amount:</Typography>
                    <Typography>LKR {totals.discount_amount}</Typography>
                  </Box>
                  
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography>VAT Rate:</Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <TextField
                        size="small"
                        type="number"
                        value={vatRate}
                        onChange={handleVatRateChange}
                        inputProps={{ min: 0, max: 100, step: 0.1 }}
                        sx={{ width: 100 }}
                      />
                      <Typography>%</Typography>
                    </Box>
                  </Box>
                  
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography>VAT Amount:</Typography>
                    <Typography>LKR {totals.tax_amount}</Typography>
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="h6">Total:</Typography>
                    <Typography variant="h6" color="primary">
                      LKR {totals.total}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/invoices')}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={saving || !formData.customer_id || formData.items.length === 0}
                >
                  {saving ? 'Saving...' : isEditMode ? 'Update Invoice' : 'Create Invoice'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Dialogs */}
      <NewCustomerDialog />
      <ProductSearchDialog />
    </Container>
  );
};

export default InvoiceForm;